<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import BarChart from 'shared/components/charts/BarChart.vue';
import ReportMetricCard from '../ReportMetricCard.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import CrmDealsReportsAPI from 'dashboard/api/reports/crmDeals';
import Multiselect from 'vue-multiselect';

const { t } = useI18n();

// Estados
const customDateRange = ref([new Date(), new Date()]);
// Agregados calculados en el backend (V2::Reports::Crm::DealsBuilder), acotados al rango.
const summary = ref(null);
const isLoading = ref(false);

// Constantes de filtros de fecha
const DATE_FILTERS = {
  TODAY: 1,
  LAST_WEEK: 2,
  LAST_MONTH: 3,
  CUSTOM: 4
};

const dateFilterOptions = computed(() => [
  { id: DATE_FILTERS.TODAY, name: t('CRM_REPORTS.LEADS.FILTERS.TODAY') },
  { id: DATE_FILTERS.LAST_WEEK, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_WEEK') },
  { id: DATE_FILTERS.LAST_MONTH, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_MONTH') },
  { id: DATE_FILTERS.CUSTOM, name: t('CRM_REPORTS.LEADS.FILTERS.CUSTOM') }
]);

const selectedDateFilter = ref(dateFilterOptions.value[2]); // Default: Last Month

// Función para obtener todos los deals
// Computed - Calcular rango de fechas según filtro seleccionado
const dateRange = computed(() => {
  if (!selectedDateFilter.value) return { start: new Date(), end: new Date() };
  
  const today = new Date();
  
  switch (selectedDateFilter.value.id) {
    case DATE_FILTERS.TODAY:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_WEEK:
      return {
        start: startOfDay(subDays(today, 7)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_MONTH:
      return {
        start: startOfDay(subDays(today, 30)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.CUSTOM:
      return {
        start: startOfDay(customDateRange.value[0]),
        end: endOfDay(customDateRange.value[1])
      };
    default:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
  }
});

const fetchSummary = async () => {
  const { start, end } = dateRange.value;
  isLoading.value = true;
  try {
    const { data } = await CrmDealsReportsAPI.getSummary({
      since: Math.floor(start.getTime() / 1000),
      until: Math.floor(end.getTime() / 1000),
      sections: ['products']
    });
    summary.value = data;
  } catch (error) {
    summary.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(dateRange, fetchSummary, { immediate: true });

// Unidades por producto, sumadas desde crm_deal_products sobre los negocios ganados del
// rango. Antes se leía `deal.product_id`, columna deprecada que hoy siempre viene nula,
// así que esta pantalla mostraba cero para cualquier negocio creado desde la UI.
const unitsSoldByProduct = computed(() =>
  (summary.value?.products || []).map(product => ({
    productId: product.product_id,
    productName: product.name,
    productSku: product.sku,
    category: product.category,
    units: product.units
  }))
);

// Computed - Total de unidades vendidas
const totalUnitsSold = computed(() =>
  unitsSoldByProduct.value.reduce((sum, product) => sum + product.units, 0)
);

// Computed - Mix de productos (porcentaje del total)
const productMix = computed(() => {
  if (totalUnitsSold.value === 0) return [];

  return unitsSoldByProduct.value.map(product => ({
    ...product,
    percentage: (product.units / totalUnitsSold.value) * 100
  }));
});

// Computed - Chart data para unidades vendidas
const unitsSoldChartData = computed(() => {
  const labels = unitsSoldByProduct.value.map(p => p.productName);
  const data = unitsSoldByProduct.value.map(p => p.units);
  
  return {
    labels,
    datasets: [{
      label: 'Unidades Vendidas',
      data,
      backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue-500
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1
    }]
  };
});

// Computed - Chart data para mix de productos (100% apilado)
const productMixChartData = computed(() => {
  // Crear un dataset por cada producto para el gráfico 100% apilado
  const colors = [
    { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' }, // blue
    { bg: 'rgba(34, 197, 94, 0.8)', border: 'rgba(34, 197, 94, 1)' }, // green
    { bg: 'rgba(168, 85, 247, 0.8)', border: 'rgba(168, 85, 247, 1)' }, // purple
    { bg: 'rgba(234, 179, 8, 0.8)', border: 'rgba(234, 179, 8, 1)' }, // yellow
    { bg: 'rgba(239, 68, 68, 0.8)', border: 'rgba(239, 68, 68, 1)' }, // red
    { bg: 'rgba(20, 184, 166, 0.8)', border: 'rgba(20, 184, 166, 1)' }, // teal
    { bg: 'rgba(249, 115, 22, 0.8)', border: 'rgba(249, 115, 22, 1)' }, // orange
    { bg: 'rgba(236, 72, 153, 0.8)', border: 'rgba(236, 72, 153, 1)' }, // pink
  ];
  
  const datasets = productMix.value.map((product, index) => ({
    label: product.productName,
    data: [product.percentage],
    backgroundColor: colors[index % colors.length].bg,
    borderColor: colors[index % colors.length].border,
    borderWidth: 1
  }));
  
  return {
    labels: ['Mix de Productos'],
    datasets
  };
});

// Chart options para unidades vendidas
const unitsSoldChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context) => {
          return `Unidades: ${context.parsed.y}`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
};

// Chart options para mix de productos (100% apilado)
const productMixChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: {
    legend: {
      display: true,
      position: 'right'
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context) => {
          const product = productMix.value[context.datasetIndex];
          return `${context.dataset.label}: ${product.percentage.toFixed(1)}% (${product.units} unidades)`;
        }
      }
    }
  },
  scales: {
    x: {
      stacked: true,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`
      }
    },
    y: {
      stacked: true
    }
  }
};

const onDateRangeChange = (value) => {
  customDateRange.value = value;
};

watch(selectedDateFilter, (newValue) => {
  if (newValue && newValue.id !== DATE_FILTERS.CUSTOM) {
    customDateRange.value = [new Date(), new Date()];
  }
}, { deep: true });
</script>

<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="flex items-start justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('CRM_REPORTS.PRODUCTS.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {{ $t('CRM_REPORTS.PRODUCTS.DESCRIPTION') }}
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="multiselect-wrap--small w-48">
            <multiselect
              v-model="selectedDateFilter"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
              :options="dateFilterOptions"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
            />
          </div>
          
          <WootDateRangePicker
            v-if="selectedDateFilter && selectedDateFilter.id === DATE_FILTERS.CUSTOM"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>
      </div>

      <!-- Card de métrica principal -->
      <div class="mb-6">
        <ReportMetricCard
          header="Total de Unidades Vendidas"
          info-text="Cantidad total de productos vendidos en el período seleccionado"
        >
          <h3 class="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {{ totalUnitsSold }}
          </h3>
        </ReportMetricCard>
      </div>

      <!-- Gráfico de Unidades Vendidas por Producto -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Unidades Vendidas por Producto
        </h4>
        <div class="h-80">
          <BarChart v-if="unitsSoldByProduct.length > 0" :collection="unitsSoldChartData" :chart-options="unitsSoldChartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de ventas con productos para el período seleccionado
          </div>
        </div>
      </div>

      <!-- Tabla detallada de unidades vendidas -->
      <div v-if="unitsSoldByProduct.length > 0" class="mt-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Detalle por Producto
        </h4>
        <div class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table class="w-full">
            <thead class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Producto
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  SKU
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Categoría
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Unidades
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Mix
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
              <tr 
                v-for="product in productMix" 
                :key="product.productId"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td class="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ product.productName }}
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                  {{ product.productSku || '-' }}
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                  {{ product.category || '-' }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-blue-600 dark:text-blue-400 text-right">
                  {{ product.units }}
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                  {{ product.percentage.toFixed(1) }}%
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <td colspan="3" class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Total
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-blue-600 dark:text-blue-400 text-right">
                  {{ totalUnitsSold }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Mix de Productos -->
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Mix de Productos
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Distribución porcentual de ventas por producto
        </p>
      </div>

      <!-- Gráfico 100% apilado -->
      <div v-if="productMix.length > 0" class="mb-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Gráfico de Distribución (100% Apilado)
        </h4>
        <div class="h-40">
          <BarChart :collection="productMixChartData" :chart-options="productMixChartOptions" />
        </div>
      </div>

      <!-- Mensaje si no hay datos -->
      <div v-else class="flex items-center justify-center h-40 text-slate-500 dark:text-slate-400">
        No hay datos de ventas con productos para el período seleccionado
      </div>

      <!-- Tabla de porcentajes -->
      <div v-if="productMix.length > 0" class="mt-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Porcentaje por Producto
        </h4>
        <div class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table class="w-full">
            <thead class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Producto
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Unidades
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  % del Total
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
              <tr 
                v-for="(product, index) in productMix" 
                :key="product.productId"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div 
                      class="w-4 h-4 rounded"
                      :style="{ backgroundColor: productMixChartData.datasets[index].backgroundColor }"
                    ></div>
                    <span class="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {{ product.productName }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right">
                  {{ product.units }}
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                    {{ product.percentage.toFixed(1) }}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.multiselect-wrap--small {
  min-width: 12rem;
}
</style>
